import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from "@reduxjs/toolkit"
import { defaultEditorPreferences, defaultProjectSettings, IEditorPreferences, IProjectSettings } from '../../interfaces/Preferences';
import { Subset } from '../../interfaces/Misc';
import { copyValueOrObject, JsonCopy } from '../../classes/utilities/Misc';
import { Modifier } from '../../interfaces/Input';

export interface IPreferencesState {
	editorPreferences: IEditorPreferences;
	projectSettings: IProjectSettings;
	keybindingEditor: {
		/** the id of the keybinding being edited by the user */
		editTarget: string;
		/** when the user tries to assign an invalid keybindings, this message will appear */
		assignmentError: string;
	},
}

const initialState: IPreferencesState = {
	editorPreferences: JsonCopy(defaultEditorPreferences),
	projectSettings: JsonCopy(defaultProjectSettings),
	keybindingEditor: {
		editTarget: "",
		assignmentError: "",
	},	
};

const PreferencesSlice = createSlice({
	name: "preferences",
	initialState: { ...initialState },
	reducers: {
		loadData(state, action: PayloadAction<IPreferencesState>)
		{
			const payloadCopy = copyValueOrObject(action.payload);
			return payloadCopy;
		},
		setEditorPreferences(state, action: PayloadAction<Subset<IEditorPreferences>>)
		{
			const payloadCopy = copyValueOrObject(action.payload);
			state.editorPreferences = { ...state.editorPreferences, ...payloadCopy } as IEditorPreferences;
		},
		resetEditorPreferencesToDefault(state, action: PayloadAction<Array<keyof IEditorPreferences>>)
		{
			if(!Array.isArray(action.payload) || !action.payload.length) return;
			
			for(const key of action.payload)
			{
				(state.editorPreferences as any)[key] = copyValueOrObject(initialState.editorPreferences[key]);
			}
		},
		resetAllEditorPreferences(state, action: PayloadAction<undefined>)
		{
			state.editorPreferences = JsonCopy(initialState.editorPreferences);
		},
		setProjectSettings(state, action: PayloadAction<Subset<IProjectSettings>>)
		{
			const payloadCopy = copyValueOrObject(action.payload);
			state.projectSettings = { ...state.projectSettings, ...payloadCopy } as IProjectSettings;
		},
		resetProjectSettingsToDefault(state, action: PayloadAction<Array<keyof IProjectSettings>>)
		{
			if(!Array.isArray(action.payload) || !action.payload.length) return;
			
			for(const key of action.payload)
			{
				(state.projectSettings as any)[key] = copyValueOrObject(initialState.projectSettings[key]);
			}
		},
		resetAllProjectSettings(state, action: PayloadAction<undefined>)
		{
			state.projectSettings = JsonCopy(initialState.projectSettings);
		},
		resetAll(state, action: PayloadAction<undefined>)
		{
			return JsonCopy(initialState);
		},
		setKeybindingValue(state, action: PayloadAction<{ id: string, keycode: number, modifiers: Modifier[] }>)
		{
			const { id, keycode, modifiers } = action.payload;

			if(!state.editorPreferences.keybindings[id]) return;

			state.editorPreferences.keybindings[id].keycode = keycode;
			state.editorPreferences.keybindings[id].modifiers = [ ...modifiers ];
		},
		setKeybindEditTarget(state, action: PayloadAction<string>)
		{
			state.keybindingEditor.editTarget = action.payload;
		},
		setKeybindAssignmentError(state, action: PayloadAction<string>)
		{
			state.keybindingEditor.assignmentError = action.payload;
		},
	},
});

export default PreferencesSlice;
